/** Subconjunto de MapDataPoint — evita import circular com UnifiedMapView. */
export type DispersibleMapPoint = {
  id: string;
  type: string;
  lat: number;
  lng: number;
};

const MIN_SEP_M = 320;

function metersBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dx = (aLat - bLat) * 111000;
  const dy = (aLng - bLng) * 111000 * Math.cos(((aLat + bLat) / 2) * Math.PI / 180);
  return Math.sqrt(dx * dx + dy * dy);
}

function stringHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

/** Chave estável da escola a partir do id do ponto (wizard vs OverviewPanel). */
export function schoolKeyFromPointId(id: string): string {
  return id.startsWith('school-') ? id.slice('school-'.length) : id;
}

function pointBelongsToSchool(pointId: string, schoolKey: string): boolean {
  if (pointId === schoolKey || pointId === `school-${schoolKey}`) return true;
  if (pointId.startsWith(`${schoolKey}-`)) return true;
  if (pointId.startsWith(`student-${schoolKey}-`)) return true;
  if (pointId.includes(`-${schoolKey}-`)) return true;
  return false;
}

/**
 * Se escolas estão empilhadas (mesma coord / muito próximas), redistribui em anel
 * e desloca residências/alunos/responsáveis ligados à mesma escola.
 * Usado pelo UnifiedMapView — vale para wizard e OverviewPanel.
 */
export function disperseStackedMapPoints<T extends DispersibleMapPoint>(
  points: T[],
  minSepM = MIN_SEP_M,
): T[] {
  const schools = points.filter(
    (p) =>
      p.type === 'SCHOOL' &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lng) &&
      p.lat !== 0 &&
      p.lng !== 0,
  );
  if (schools.length < 2) return points;

  let closePairs = 0;
  let checks = 0;
  for (let i = 0; i < schools.length; i++) {
    for (let j = i + 1; j < schools.length; j++) {
      checks++;
      if (metersBetween(schools[i].lat, schools[i].lng, schools[j].lat, schools[j].lng) < minSepM) {
        closePairs++;
      }
    }
  }
  if (checks === 0 || closePairs / checks < 0.35) return points;

  const anchorLat = schools.reduce((s, p) => s + p.lat, 0) / schools.length;
  const anchorLng = schools.reduce((s, p) => s + p.lng, 0) / schools.length;
  const cosLat = Math.cos(anchorLat * Math.PI / 180);

  type Move = { dLat: number; dLng: number; lat: number; lng: number };
  const moves = new Map<string, Move>();
  const placed: { lat: number; lng: number }[] = [];

  const pref = points.find((p) => p.type === 'PREFEITURA');
  if (pref && Number.isFinite(pref.lat) && Number.isFinite(pref.lng)) {
    placed.push({ lat: pref.lat, lng: pref.lng });
  }

  // Cluster empilhado confirmado: redistribui todas as escolas em anel
  schools.forEach((school, i) => {
    const key = schoolKeyFromPointId(school.id);
    const hash = stringHash(school.id);
    let lat = school.lat;
    let lng = school.lng;

    const tooClose = () => placed.some((p) => metersBetween(lat, lng, p.lat, p.lng) < minSepM);

    for (let attempt = 0; attempt < Math.max(schools.length * 2, 8); attempt++) {
      const angle =
        (2 * Math.PI * (i + attempt)) / Math.max(schools.length, 1) +
        ((hash % 47) / 47) * 0.4;
      const distM = 850 + ((i + attempt) % 5) * 280 + (hash % 90);
      lat = anchorLat + (distM / 111000) * Math.sin(angle);
      lng = anchorLng + (distM / (111000 * cosLat)) * Math.cos(angle);
      if (!tooClose()) break;
    }

    moves.set(key, {
      dLat: lat - school.lat,
      dLng: lng - school.lng,
      lat,
      lng,
    });
    placed.push({ lat, lng });
  });

  return points.map((p) => {
    if (p.type === 'SCHOOL') {
      const move = moves.get(schoolKeyFromPointId(p.id));
      if (!move) return p;
      return { ...p, lat: move.lat, lng: move.lng };
    }
    if (p.type === 'PREFEITURA') return p;

    for (const [key, move] of moves) {
      if (pointBelongsToSchool(p.id, key) && (move.dLat !== 0 || move.dLng !== 0)) {
        return { ...p, lat: p.lat + move.dLat, lng: p.lng + move.dLng };
      }
    }
    return p;
  });
}
